import { ArrowLeft, MessageCircle } from "lucide-react";
import React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useI18n } from "@/context/i18n";

import { WhatsappMetaTemplateExpandable } from "./components";
import { useWhatsappMetaConfigDetail } from "./whatsapp-meta-config-detail.hooks";

function formatMetaCell(value: unknown): string {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  return JSON.stringify(value);
}

export const WhatsappMetaConfigDetailPage: React.FC = () => {
  const { t } = useI18n();
  const hook = useWhatsappMetaConfigDetail();

  const phoneRows = hook.phoneNumbersData?.data ?? [];
  const templateRows = React.useMemo(
    () =>
      hook.templatesPages?.flatMap((page) => page.data ?? []) ?? [],
    [hook.templatesPages]
  );

  const showContent =
    !hook.isConfigLoading &&
    !hook.isConfigsError &&
    hook.config &&
    hook.configId;

  return (
    <div className="flex min-h-full w-full flex-1 flex-col bg-background">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 md:px-6 md:py-8">
        <div className="flex flex-col gap-4 border-b border-border/60 pb-6">
          <Button
            type="button"
            variant="ghost"
            className="w-fit gap-2 px-0 text-muted-foreground hover:text-foreground"
            onClick={hook.backToList}
          >
            <ArrowLeft className="h-4 w-4" />
            {t("campaigns.whatsappMeta.backToList")}
          </Button>
          <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <MessageCircle className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0 space-y-1">
                <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">
                  {hook.isConfigLoading
                    ? t("generic.loading")
                    : hook.config
                      ? hook.config.label?.trim() || hook.config.waba_id
                      : t("campaigns.whatsappMeta.notFound")}
                </h1>
                {showContent ? (
                  <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
                    <span className="text-muted-foreground">
                      {t("campaigns.whatsappMeta.table.wabaId")}
                    </span>
                    {": "}
                    <span className="font-mono text-foreground/90">{hook.config.waba_id}</span>
                    {" · "}
                    <span className="text-muted-foreground">
                      {t("campaigns.whatsappMeta.table.token")}
                    </span>
                    {": "}
                    {hook.config.has_access_token
                      ? t("campaigns.whatsappMeta.tokenConfigured")
                      : t("campaigns.whatsappMeta.tokenMissing")}
                  </p>
                ) : null}
              </div>
            </div>
          </header>
        </div>

        {hook.isConfigLoading ? (
          <p className="text-sm text-muted-foreground">{t("generic.loading")}</p>
        ) : hook.isConfigsError ? (
          <div
            className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            role="alert"
          >
            <p className="font-medium">{t("campaigns.whatsappMeta.errors.loadConfigs")}</p>
            {hook.configsErrorMessage ? (
              <p className="mt-1 text-destructive/90">{hook.configsErrorMessage}</p>
            ) : null}
          </div>
        ) : hook.organizationId && hook.configId && hook.config === null ? (
          <div className="flex flex-col gap-4 rounded-lg border border-border bg-muted/10 px-4 py-8">
            <p className="text-center text-sm text-muted-foreground">
              {t("campaigns.whatsappMeta.notFound")}
            </p>
            <Button type="button" variant="outline" className="mx-auto w-full max-w-xs" onClick={hook.backToList}>
              {t("campaigns.whatsappMeta.backToList")}
            </Button>
          </div>
        ) : showContent ? (
          <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
            <Card className="border-border/80 shadow-sm">
              <CardHeader className="border-b border-border/60 pb-4">
                <CardTitle className="text-lg font-semibold">
                  {t("campaigns.whatsappMeta.phoneNumbersTitle")}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {hook.isPhoneNumbersLoading ? (
                  <p className="text-sm text-muted-foreground">
                    {t("generic.loading")}
                  </p>
                ) : !phoneRows.length ? (
                  <p className="text-sm text-muted-foreground">
                    {t("campaigns.whatsappMeta.phoneNumbersEmpty")}
                  </p>
                ) : (
                  <div className="-mx-1 overflow-x-auto px-1">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>
                            {t("campaigns.whatsappMeta.phoneTable.display")}
                          </TableHead>
                          <TableHead>
                            {t("campaigns.whatsappMeta.phoneTable.verifiedName")}
                          </TableHead>
                          <TableHead>
                            {t("campaigns.whatsappMeta.phoneTable.id")}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {phoneRows.map((item, idx) => (
                          <TableRow key={formatMetaCell(item.id) + String(idx)}>
                            <TableCell className="text-sm">
                              {formatMetaCell(item.display_phone_number)}
                            </TableCell>
                            <TableCell className="text-sm">
                              {formatMetaCell(item.verified_name)}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                              {formatMetaCell(item.id)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="border-border/80 shadow-sm lg:col-span-2">
              <CardHeader className="border-b border-border/60 pb-4">
                <CardTitle className="text-lg font-semibold">
                  {t("campaigns.whatsappMeta.templatesTitle")}
                </CardTitle>
                <p className="text-sm font-normal text-muted-foreground">
                  {t("campaigns.whatsappMeta.templatesDetail.hint")}
                </p>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                {hook.isTemplatesLoading ? (
                  <p className="text-sm text-muted-foreground">
                    {t("generic.loading")}
                  </p>
                ) : !templateRows.length ? (
                  <p className="text-sm text-muted-foreground">
                    {t("campaigns.whatsappMeta.templatesEmpty")}
                  </p>
                ) : (
                  <>
                    <div className="space-y-2">
                      {templateRows.map((item, idx) => (
                        <WhatsappMetaTemplateExpandable
                          key={formatMetaCell(item.id) + String(idx)}
                          template={item}
                        />
                      ))}
                    </div>
                    {hook.hasNextTemplatesPage ? (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full"
                        disabled={hook.isFetchingTemplatesNextPage}
                        onClick={() => hook.fetchNextTemplatesPage()}
                      >
                        {hook.isFetchingTemplatesNextPage
                          ? t("generic.loading")
                          : t("campaigns.whatsappMeta.loadMoreTemplates")}
                      </Button>
                    ) : null}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        ) : null}
      </div>
    </div>
  );
};
